/**
 * UI-8c Task 8 — KeyRotationPage.
 */
import { useState, type JSX } from 'react'
import { Column } from 'primereact/column'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'
import {
  KeyRound, RefreshCw, Calendar, History, Shield,
  AlertTriangle, CheckCircle2, Clock,
} from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import { ScheduleRotationDialog } from '../../components/platform/ScheduleRotationDialog.js'
import { RotationHistoryDrawer } from '../../components/platform/RotationHistoryDrawer.js'
import {
  useKeyRotation,
  type KeyRotationStatus,
} from '../../hooks/useKeyRotation.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { formatDateTime } from '../../utils/formatDate.js'
import DataTable from '../../components/ui/DataTable.js'
import { timestampMs } from '../../utils/timezone.js'

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000
const POST_TRIGGER_REFETCH_MS = 60_000

/* ─── Stat Card ─── */
function StatCard({ label, value, icon: Icon, accent }: {
  label: string
  value: number
  icon: typeof KeyRound
  accent: string
}) {
  return (
    <div className="relative bg-white rounded-sm border border-gray-100 p-5 overflow-hidden group hover:shadow-md hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${accent}10` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: accent }} />
    </div>
  )
}

/* ─── Status Badge ─── */
function StatusBadge({ t }: { t: KeyRotationStatus }) {
  if (t.scheduledFor) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
        <Clock className="h-3 w-3" /> Scheduled
      </span>
    )
  }
  if (!t.lastRotationAt) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
        <span className="h-1.5 w-1.5 rounded-full bg-gray-400" /> Never rotated
      </span>
    )
  }
  const age = Date.now() - timestampMs(t.lastRotationAt)
  if (age > NINETY_DAYS_MS) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700">
        <AlertTriangle className="h-3 w-3" /> Stale
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
      <CheckCircle2 className="h-3 w-3" /> Current
    </span>
  )
}

export default function KeyRotationPage(): JSX.Element {
  const toast = useToast()
  const { tenants, loading, refetch, trigger, schedule } = useKeyRotation()
  const [scheduleFor, setScheduleFor] = useState<KeyRotationStatus | null>(null)
  const [historyFor, setHistoryFor] = useState<KeyRotationStatus | null>(null)

  function handleRotateNow(t: KeyRotationStatus): void {
    confirmDialog({
      message: `Rotate DEKs for ${t.slug} now? This re-encrypts every recording with a new DEK.`,
      header: `Rotate ${t.name || t.slug}`,
      icon: 'pi pi-refresh',
      acceptLabel: 'Rotate Now',
      acceptClassName: 'p-button-warning',
      accept: async () => {
        try {
          await trigger(t.slug)
          toast.success(`Rotation enqueued for ${t.slug}`)
          window.setTimeout(() => { void refetch() }, POST_TRIGGER_REFETCH_MS)
        } catch (e) {
          toast.error(e instanceof Error ? e.message : 'Failed to enqueue rotation')
        }
      },
    })
  }

  async function handleScheduleSubmit(when: Date): Promise<void> {
    const target = scheduleFor
    if (!target) return
    try {
      await schedule(target.slug, when)
      toast.success(`Rotation scheduled for ${target.slug} at ${formatDateTime(when)}`)
      setScheduleFor(null)
      await refetch()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to schedule rotation')
    }
  }

  const staleCount = tenants.filter(t => t.lastRotationAt && Date.now() - timestampMs(t.lastRotationAt) > NINETY_DAYS_MS).length
  const scheduledCount = tenants.filter(t => !!t.scheduledFor).length
  const neverCount = tenants.filter(t => !t.lastRotationAt).length

  return (
    <RequirePermission permission="platform:key_rotation">
      <div className="w-full space-y-6 px-4 pb-10">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Key Rotation</h1>
            <p className="text-sm text-gray-400 mt-1">Trigger or schedule DEK rotations per tenant. Status reflects the most recent rotation.</p>
          </div>
          <button
            onClick={() => void refetch()}
            disabled={loading}
            className="h-9 w-9 flex items-center justify-center rounded-sm text-gray-400 hover:text-[#3454cf] hover:bg-[#3454cf]/5 transition-all border border-gray-200 hover:border-[#3454cf]/20"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard label="Total Tenants" value={tenants.length} icon={Shield} accent="#3454cf" />
          <StatCard label="Stale (>90d)" value={staleCount} icon={AlertTriangle} accent="#ef4444" />
          <StatCard label="Scheduled" value={scheduledCount} icon={Clock} accent="#3b82f6" />
          <StatCard label="Never Rotated" value={neverCount} icon={KeyRound} accent="#f6931c" />
        </div>

        {/* Table */}
        <DataTable
          value={tenants}
          loading={loading}
          className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
          emptyMessage="No tenants available to rotate."
        >
          <Column
            header="Tenant"
            body={(t: KeyRotationStatus) => (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
                  <KeyRound className="h-5 w-5 text-[#3454cf]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{t.name || t.slug}</p>
                  <p className="text-xs text-gray-300">{t.slug}</p>
                </div>
              </div>
            )}
            style={{ width: '25%' }}
          />
          <Column
            header="Last Rotation"
            body={(t: KeyRotationStatus) => t.lastRotationAt ? <span className="text-xs text-gray-500">{formatDateTime(t.lastRotationAt)}</span> : <span className="text-xs text-gray-300 italic">—</span>}
          />
          <Column
            header="KEK v"
            body={(t: KeyRotationStatus) => <span className="text-sm font-medium text-gray-700 font-mono">{t.kekVersion ?? '—'}</span>}
          />
          <Column
            header="DEKs"
            body={(t: KeyRotationStatus) => <span className="text-sm font-medium text-gray-700">{t.dekCount ?? '—'}</span>}
          />
          <Column
            header="Status"
            body={(t: KeyRotationStatus) => <StatusBadge t={t} />}
          />
          <Column
            header="Actions"
            body={(t: KeyRotationStatus) => (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleRotateNow(t)}
                  className="inline-flex items-center gap-1.5 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-[10px] font-semibold px-2.5 py-1.5 rounded-sm transition-all"
                  title="Rotate Now"
                >
                  <RefreshCw className="h-3 w-3" /> Rotate
                </button>
                <button
                  onClick={() => setScheduleFor(t)}
                  className="h-8 w-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-[#3454cf] transition-all"
                  title="Schedule"
                >
                  <Calendar className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setHistoryFor(t)}
                  className="h-8 w-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-[#3454cf] transition-all"
                  title="History"
                >
                  <History className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            style={{ width: '180px' }}
          />
        </DataTable>

        <ScheduleRotationDialog
          open={scheduleFor !== null}
          tenantSlug={scheduleFor?.slug ?? ''}
          onClose={() => setScheduleFor(null)}
          onSubmit={(when) => void handleScheduleSubmit(when)}
        />

        <RotationHistoryDrawer
          open={historyFor !== null}
          tenantSlug={historyFor?.slug ?? ''}
          onClose={() => setHistoryFor(null)}
        />

        <ConfirmDialog />
      </div>
    </RequirePermission>
  )
}
